/*
  Ghostboy "Complete the look" — matching sets (ClickUp z93r0b9kyq; docs/SETS.md).

  <product-set>  (product page block, snippets/product-set.liquid)
    One column per piece, each with the theme's <product-variants> picker
    (rendered by snippets/quick-buy.liquid in defer_purchase mode). Keeps
    the set total in step with the selections, mirrors the main size picker
    into the current product's row, and adds every checked variant in one
    request through window.handleAddToCart (component-quick-buy.js), so the
    drawer opens exactly like any other add-to-cart. Every piece is part of
    the set — there is no opt-out tick (Cyii).
    The percent shown here is display only: the set-discount Shopify Function
    applies the real discount from the same shop metafield.

  <cart-complete-the-look>  (cart drawer / page, snippets/cart-complete-the-look.liquid)
    The drawer is re-rendered with innerHTML on every cart change, which never
    executes the inline <script> that quick-buy uses to append its hidden
    variant input. This element re-applies that <template> so the row's Add
    button always posts a variant id.
*/

(function () {

  function appendQuickBuyTemplates(root) {
    root.querySelectorAll('form[data-type="add-to-cart-form"]').forEach(function (form) {
      if (form.querySelector('input[name="id"]')) return;
      var template = form.querySelector('template');
      if (template) form.append(template.content.cloneNode(true));
    });
  }

  if (typeof ProductSet !== 'function') {

    class ProductSet extends HTMLElement {

      connectedCallback() {
        if (this._mounted) return;
        this._mounted = true;

        appendQuickBuyTemplates(this);

        this.percent = parseFloat(this.dataset.percent) || 0;
        this.rows = Array.from(this.querySelectorAll('[data-set-row]')).map(function (row) {
          return {
            el: row,
            variants: row.querySelector('product-variants'),
            form: row.querySelector('form')
          };
        });
        this.totalEl = this.querySelector('[data-set-total]');
        this.fullTotalHtml = this.totalEl.innerHTML; // server-rendered sum of every piece
        this.fullTotalCents = parseInt(this.dataset.fullTotal, 10) || 0;
        // fx-estimates.js is deferred too and may upgrade after us: refill once the page has loaded
        window.addEventListener('load', this.update.bind(this), { once: true });
        this.compareEl = this.querySelector('[data-set-total-compare]');
        this.savingEl = this.querySelector('[data-set-saving]');
        this.estimateEl = this.querySelector('[data-set-total-estimate]');
        this.button = this.querySelector('[data-set-add]');
        this.buttonText = this.querySelector('[data-set-add-text]');

        this.rows.forEach(function (row) {
          if (row.variants) {
            row.variants.addEventListener('VARIANT_CHANGE', this.update.bind(this));
          }
        }, this);

        this.followMainPicker();
        this.button.addEventListener('click', this.addToCart.bind(this));
        this.update();
      }

      // The current product's row mirrors the main size picker (main → row only).
      followMainPicker() {
        var main = document.querySelector('product-variants[data-main-product-variants][data-id="' + this.dataset.mainId + '"]');
        var row = this.rows.find(function (r) { return r.el.hasAttribute('data-set-main'); });
        if (!main || !row || !row.variants) return;
        var sync = function () {
          var variant = main.currentVariant;
          if (!variant || !variant.options) return;
          var selects = row.variants.querySelectorAll('[data-js-product-variant-container="select"]');
          var changed = false;
          variant.options.forEach(function (value, index) {
            var select = selects[index];
            if (select && select.value !== value) {
              select.value = value;
              changed = true;
            }
          });
          if (changed) {
            // the picker listens for a bubbling change on itself
            selects[0].dispatchEvent(new Event('change', { bubbles: true }));
          }
        };
        main.addEventListener('VARIANT_CHANGE', sync);
        sync();
      }

      // Selected variant for a row: the picker's choice, or the only variant of
      // a one-size product (its hidden input holds the id).
      rowVariant(row) {
        var pv = row.variants;
        if (!pv) return null;
        if (pv.currentVariant) return pv.currentVariant;
        if (!pv.hasAttribute('data-has-variants') && !pv.hasAttribute('data-unavailable')) {
          var input = row.form && row.form.querySelector('input[name="id"]');
          var id = input && parseInt(input.value, 10);
          var data = pv.getVariantData ? pv.getVariantData() : [];
          return data.find(function (v) { return v.id === id; }) || null;
        }
        return null;
      }

      // Every piece is part of the set (no opt-out): the set is whole when
      // each row has an available variant selected.
      state() {
        var items = [];
        var missing = false;   // a row has no size chosen
        var soldOut = false;   // a row's chosen size is out of stock
        var total = 0;
        this.rows.forEach(function (row) {
          var variant = this.rowVariant(row);
          if (!variant) { missing = true; return; }
          if (!variant.available) { soldOut = true; return; }
          total += variant.price;
          items.push({ id: variant.id, quantity: 1 });
        }, this);
        var blocked = missing || soldOut;
        var whole = !blocked && this.rows.length > 1;
        return { items: items, missing: blocked, soldOut: soldOut, total: total, whole: whole };
      }

      formatMoney(cents) {
        var pv = this.querySelector('product-variants');
        return pv && pv._formatMoney ? pv._formatMoney(cents, KROWN.settings.shop_money_format) : (cents / 100).toFixed(2);
      }

      update() {
        var s = this.state();
        var discounted = s.whole && this.percent > 0;
        var payable = discounted ? Math.round(s.total * (100 - this.percent) / 100) : s.total;

        this.totalEl.innerHTML = s.missing ? this.fullTotalHtml : this.formatMoney(payable);
        if (this.compareEl) {
          this.compareEl.hidden = !(discounted && !s.missing);
          if (discounted) this.compareEl.innerHTML = this.formatMoney(s.total);
        }
        if (this.savingEl) this.savingEl.hidden = !(discounted && !s.missing);
        this.updateEstimate(s.missing ? this.fullTotalCents : payable);

        var disabled = s.missing || s.items.length === 0;
        this.button.classList.toggle('disabled', disabled);
        this.button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        this.buttonText.textContent = s.soldOut
          ? this.button.dataset.labelSoldOut
          : (s.missing ? this.button.dataset.labelSizes : this.button.dataset.labelSet);
      }

      // Overseas visitors: approximate local-currency amount beside the MYR total
      // (KROWN.fx.estimate is defined by assets/fx-estimates.js only when a
      // non-Malaysian currency was resolved; otherwise the span stays hidden).
      updateEstimate(cents) {
        if (!this.estimateEl) return;
        var fx = window.KROWN && KROWN.fx && KROWN.fx.estimate;
        var text = fx && cents > 0 ? fx(cents) : null;
        if (text) {
          this.estimateEl.textContent = text;
          this.estimateEl.hidden = false;
        } else {
          this.estimateEl.textContent = '';
          this.estimateEl.hidden = true;
        }
      }

      addToCart() {
        var s = this.state();
        if (s.missing || !s.items.length || this.button.classList.contains('working')) return;
        this.button.classList.add('working');
        window.handleAddToCart(
          JSON.stringify({ items: s.items }), s.items.length,
          { 'Content-Type': 'application/json' },
          function () { this.button.classList.remove('working'); }.bind(this)
        );
      }

    }

    if (typeof customElements.get('product-set') == 'undefined') {
      customElements.define('product-set', ProductSet);
    }

  }

  if (typeof CartCompleteTheLook !== 'function') {

    class CartCompleteTheLook extends HTMLElement {
      connectedCallback() {
        appendQuickBuyTemplates(this);
      }
    }

    if (typeof customElements.get('cart-complete-the-look') == 'undefined') {
      customElements.define('cart-complete-the-look', CartCompleteTheLook);
    }

  }

})();
